import { FC, ReactNode, useMemo, useState } from 'react';
import { Button, Divider, Popover, Space, Tag, Typography } from 'antd';
import { TbArrowDownRight, TbArrowRight, TbCalendarDollar, TbExternalLink, TbNews } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownPanel from '@/shared/components/dropdowns/DropdownPanel';
import { dayjs } from '@/utilities';

import { FinancialOverviewChartEvent, FinancialOverviewKeyEventKey } from '../../types';
import { getFinancialOverviewEventDestination } from './destinations';

interface FinancialOverviewChartEventMarkerViewBox {
    height?: number;
    width?: number;
    x?: number;
    y?: number;
}

interface FinancialOverviewChartEventMarkerProps {
    events: FinancialOverviewChartEvent[];
    tickerSymbol?: string;
    viewBox?: FinancialOverviewChartEventMarkerViewBox;
}

interface FinancialOverviewChartEventDetailsProps {
    events: FinancialOverviewChartEvent[];
    onAction: () => void;
    tickerSymbol?: string;
}

const EVENT_LABELS: Record<FinancialOverviewKeyEventKey, string> = {
    dividends: 'Dividend',
    news: 'Company News',
    recessions: 'Recession',
};

const eventIcon = (type: FinancialOverviewKeyEventKey): ReactNode => {
    if (type === 'news') {
        return <TbNews />;
    }

    if (type === 'dividends') {
        return <TbCalendarDollar />;
    }

    return <TbArrowDownRight />;
};

const safeExternalUrl = (value?: string) => {
    if (!value) {
        return undefined;
    }

    try {
        const url = new URL(value);

        return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : undefined;
    } catch {
        return undefined;
    }
};

const formatEventDate = (date?: string, type?: FinancialOverviewKeyEventKey) =>
    date && dayjs(date).isValid() ? dayjs(date).format(type === 'recessions' ? 'MMMM YYYY' : 'MMM D, YYYY') : '-';

const FinancialOverviewChartEventDetails: FC<FinancialOverviewChartEventDetailsProps> = ({
    events,
    onAction,
    tickerSymbol,
}) => {
    const { token } = useAntDesignTheme();
    const navigate = useNavigate();
    // viewport-layout-allow: the event menu is a floating overlay constrained to the browser edge.
    const maxHeight = 'min(22rem, 60vh)';
    // viewport-layout-allow: the event menu is a floating overlay constrained to the browser edge.
    const width = 'min(26rem, calc(100vw - 2rem))';

    return (
        <DropdownPanel
            header={
                <div style={{ padding: '0.625rem 0.75rem' }}>
                    <Typography.Text strong>
                        {events.length.toLocaleString()} Key {events.length === 1 ? 'Event' : 'Events'}
                    </Typography.Text>
                    <div style={{ color: token.colorTextTertiary, fontSize: token.fontSizeSM }}>
                        {formatEventDate(events[0]?.date, events[0]?.type)}
                    </div>
                </div>
            }
            maxHeight={maxHeight}
            minWidth='22rem'
            style={{ border: 0, boxShadow: 'none' }}
            width={width}
        >
            <div style={{ padding: '0.25rem 0.75rem' }}>
                {events.map((event, index) => {
                    const externalUrl = safeExternalUrl(event.externalUrl);
                    const destination = getFinancialOverviewEventDestination(event.type, tickerSymbol);

                    return (
                        <div key={`${event.type}:${event.date}:${event.title ?? event.label}:${index}`}>
                            {index ? <Divider style={{ margin: '0.5rem 0' }} /> : null}
                            <Space align='start' size='small' style={{ width: '100%' }}>
                                <span
                                    style={{
                                        color:
                                            event.type === 'news'
                                                ? token.gold6
                                                : event.type === 'dividends'
                                                  ? token.green6
                                                  : token.colorTextSecondary,
                                        display: 'inline-flex',
                                        marginTop: '0.125rem',
                                    }}
                                >
                                    {eventIcon(event.type)}
                                </span>
                                <div style={{ minWidth: 0, width: '100%' }}>
                                    <Space size={4} wrap>
                                        <Typography.Text strong>{event.title ?? event.label}</Typography.Text>
                                        <Tag bordered={false}>{EVENT_LABELS[event.type]}</Tag>
                                    </Space>
                                    <div
                                        style={{
                                            color: token.colorTextTertiary,
                                            fontSize: token.fontSizeSM,
                                            marginTop: '0.125rem',
                                        }}
                                    >
                                        {event.endDate
                                            ? `${formatEventDate(event.date, event.type)} – ${formatEventDate(event.endDate, event.type)}`
                                            : formatEventDate(event.date, event.type)}
                                        {event.source ? ` · ${event.source}` : ''}
                                    </div>
                                    {event.amount !== undefined ? (
                                        <div style={{ color: token.colorTextSecondary, marginTop: '0.375rem' }}>
                                            {event.amount.toLocaleString('en-US', {
                                                maximumFractionDigits: 4,
                                            })}{' '}
                                            {event.currency ? `${event.currency} per share` : 'per share'}
                                            {event.recordDate ? ` · Record ${formatEventDate(event.recordDate)}` : ''}
                                            {event.paymentDate
                                                ? ` · Payable ${formatEventDate(event.paymentDate)}`
                                                : ''}
                                        </div>
                                    ) : null}
                                    {event.description ? (
                                        <Typography.Paragraph
                                            ellipsis={{ rows: 3 }}
                                            style={{ color: token.colorTextSecondary, margin: '0.375rem 0 0' }}
                                        >
                                            {event.description}
                                        </Typography.Paragraph>
                                    ) : null}
                                    {destination || externalUrl ? (
                                        <Space size='small' style={{ marginTop: '0.5rem' }} wrap>
                                            {destination ? (
                                                <Button
                                                    icon={<TbArrowRight />}
                                                    iconPosition='end'
                                                    onClick={() => {
                                                        onAction();
                                                        navigate(destination.href);
                                                    }}
                                                    size='small'
                                                >
                                                    {destination.label}
                                                </Button>
                                            ) : null}
                                            {externalUrl ? (
                                                <Button
                                                    icon={<TbExternalLink />}
                                                    iconPosition='end'
                                                    onClick={() => {
                                                        onAction();
                                                        window.open(externalUrl, '_blank', 'noopener,noreferrer');
                                                    }}
                                                    size='small'
                                                    type='text'
                                                >
                                                    Open Article
                                                </Button>
                                            ) : null}
                                        </Space>
                                    ) : null}
                                </div>
                            </Space>
                        </div>
                    );
                })}
            </div>
        </DropdownPanel>
    );
};

export const FinancialOverviewChartEventMarker: FC<FinancialOverviewChartEventMarkerProps> = ({
    events,
    tickerSymbol,
    viewBox,
}) => {
    const { token } = useAntDesignTheme();
    const [open, setOpen] = useState(false);
    const eventTypes = useMemo(() => new Set(events.map((event) => event.type)), [events]);
    const primaryType = events[0]?.type ?? 'news';
    const markerColor =
        eventTypes.size > 1
            ? token.colorPrimary
            : primaryType === 'news'
              ? token.gold6
              : primaryType === 'dividends'
                ? token.green6
                : token.colorTextSecondary;
    const markerWidth = events.length > 1 ? 38 : 28;
    const markerX = (viewBox?.x ?? 0) + (viewBox?.width ?? 0) / 2 - markerWidth / 2;
    const markerY = (viewBox?.y ?? 0) + 4;
    const dateLabel = formatEventDate(events[0]?.date, events[0]?.type);

    if (!events.length) {
        return null;
    }

    return (
        <foreignObject height={30} overflow='visible' width={markerWidth} x={markerX} y={markerY}>
            <Popover
                arrow={false}
                content={
                    <FinancialOverviewChartEventDetails
                        events={events}
                        onAction={() => setOpen(false)}
                        tickerSymbol={tickerSymbol}
                    />
                }
                destroyOnHidden
                mouseEnterDelay={0.08}
                onOpenChange={setOpen}
                open={open}
                placement='bottom'
                styles={{ body: { padding: 0 } }}
                trigger={['hover', 'click']}
            >
                <Button
                    aria-label={`View ${events.length.toLocaleString()} key ${events.length === 1 ? 'event' : 'events'} on ${dateLabel}`}
                    icon={eventIcon(primaryType)}
                    size='small'
                    style={{
                        alignItems: 'center',
                        background: token.colorBgElevated,
                        borderColor: markerColor,
                        color: markerColor,
                        display: 'inline-flex',
                        height: 26,
                        justifyContent: 'center',
                        minWidth: markerWidth,
                        pointerEvents: 'auto',
                    }}
                    type='default'
                >
                    {events.length > 1 ? events.length.toLocaleString() : null}
                </Button>
            </Popover>
        </foreignObject>
    );
};
